import { useState } from 'react';
import { useRouter } from 'next/router';

import { Alert, FormField, Heading, Icon, Label, List, Modal, Paragraph, Select } from '@zillow/constellation-10';
import { IconYardSignFilled } from '@zillow/constellation-icons';

import { AdproductsConstants } from '@zg-rentals/rental-revenue-components';
import {
  useRentalManagerUserSingleFamilyEnterpriseContractWebApiHandler_contracts,
  useRentalManagerUserSingleFamilyEnterprisePropertyWebApiHandler_customerId,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { useActivateListing } from '../../../../srcLegacy/shared/hooks/queries/useActivateListing';
import emailUtils from '../../../../srcLegacy/shared/utils/emailUtils';
import numberUtils from '../../../utils/numberUtils';
import { useGetListingDetails } from '../../../hooks/queries/properties';

import PayAndPublishCSEvents from '../../../../srcLegacy/shared/model/ClickstreamEvent/PayAndPublishCSEvents';
import SingleFamilyContractDetails from '../../../../srcLegacy/shared/model/SingleFamilyContractDetails';
import { ErrorMessageConstants } from '../../../constants/ErrorMessageConstants';

import { trackGtmEvent } from '../../../analytics';
import { ZRM_GTM_EVENTS } from '../../../analytics/events/gtm';
import { emitActivateListingEvent } from '../../../analytics/events/payAndPublish';
import { useAuth } from '../../AuthProvider';
import ModalFooter from '../ModalFooter';

import type { FreemiumDatarouterError } from '../../../../srcLegacy/shared/components/Modal/FreemiumErrorModal';

export const PreActivationModal = ({
  displayPreActivationModal = false,
  setDisplayPreActivationModal = (_display: boolean) => {},
  onSubmit,
  onActivateListing,
}: {
  displayPreActivationModal: boolean;
  setDisplayPreActivationModal: (display: boolean) => void;
  onSubmit?: () => void;
  onActivateListing?: () => void;
}) => {
  const { user } = useAuth();
  const router = useRouter();

  const { listingAlias } = router.query as { listingAlias: string };

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const {
    data: contractsData,
    isLoading: isLoadingContracts,
    error: contractsError,
  } = useRentalManagerUserSingleFamilyEnterpriseContractWebApiHandler_contracts();

  const { mutateAsync: setCustomerIdMutation } =
    useRentalManagerUserSingleFamilyEnterprisePropertyWebApiHandler_customerId();

  const { mutate: activateListing } = useActivateListing();

  const contract = listingDetails?.singleFamilyContractDto;
  const mustSetActiveContract = contract?.isRequired && !contract?.isActive;
  const defaultCustomerId = mustSetActiveContract ? (contract?.details?.customerId ?? null) : null;

  const [activationErrorMessage, setActivationErrorMessage] = useState('');
  const [customerId, setCustomerId] = useState(defaultCustomerId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | JSX.Element>('');

  const contracts = contractsData?.contracts?.map(SingleFamilyContractDetails);
  const onlyContract = contractsData?.contracts?.length === 1 ? contractsData.contracts[0] : null;
  const activeCustomerId = onlyContract?.isCustomerActive ? onlyContract.customerId : null;
  const effectiveCustomerId = customerId ?? activeCustomerId ?? null;
  const hasActiveContract = contractsData?.contracts?.some((eachContract) => eachContract.isCustomerActive) ?? false;
  const contractErrorMessage = contractsError
    ? ErrorMessageConstants.SINGLE_FAMILY_CANNOT_RETRIEVE_CONTRACTS
    : mustSetActiveContract && contractsData && !hasActiveContract
      ? ErrorMessageConstants.SINGLE_FAMILY_NO_ACTIVE_CONTRACT
      : null;

  const handleActivateListing = () => {
    activateListing(
      { getParams: { listingAlias } },
      {
        onSuccess: () => {
          if (listingDetails && user) {
            emitActivateListingEvent({ currentProperty: listingDetails, user });
          }

          onActivateListing?.();
        },
        onError: (err) => {
          const error = err as FreemiumDatarouterError;
          const newErrorMessage =
            error?.errorDetails?.listingActivationFailed && error?.errorDetails?.activationError?.message
              ? error.errorDetails.activationError.message
              : error?.message;
          setActivationErrorMessage(newErrorMessage);
        },
      },
    );
  };

  const handleSubmit = async () => {
    if (mustSetActiveContract && effectiveCustomerId === null) {
      const newErrorMessage = !hasActiveContract
        ? ErrorMessageConstants.SINGLE_FAMILY_NO_ACTIVE_CONTRACT
        : 'Select an active contract';
      setErrorMessage(newErrorMessage);
    } else if (effectiveCustomerId !== null) {
      setErrorMessage('');
      const setNewActiveContractPromise = mustSetActiveContract
        ? setCustomerIdMutation({ getParams: { listingAlias, customerId: effectiveCustomerId } })
        : Promise.resolve();
      try {
        if (mustSetActiveContract) {
          setIsSubmitting(true);
        }
        await setNewActiveContractPromise;
      } catch {
        const errMessage = `Unable to publish this property due to lack of an active contract.
                    Contact ${emailUtils.getHelpEmail(false, true)}.`;
        setErrorMessage(errMessage);
        setIsSubmitting(false);
        return;
      }

      trackGtmEvent({ event: ZRM_GTM_EVENTS.RENTAL_MANAGER_PUBLISH });

      const submitFunction = onSubmit || handleActivateListing;
      submitFunction();

      setIsSubmitting(false);
      setDisplayPreActivationModal(false);
    }
  };

  const infoList = [];

  if (!contract || mustSetActiveContract) {
    switch (true) {
      case user?.isEnterpriseCustomer:
        infoList.push('This listing will be included in your monthly invoice.');
        break;
      default:
        break;
    }
  } else {
    const formattedPrice = numberUtils.asCurrencyWithDefault(contract.details?.unitCost, '-');

    switch (contract.details?.productType) {
      case AdproductsConstants.ProductCodes.FLAT_FEE_FEED_CONNECT:
        infoList.push('This listing can be posted for free due to your monthly Zillow flat fee subscription');
        break;
      case AdproductsConstants.ProductCodes.SINGLE_FAMILY:
        infoList.push(
          `This listing will be billed at your monthly rate (${formattedPrice}) and included in your monthly invoice`,
        );
        break;
      case AdproductsConstants.ProductCodes.SINGLE_FAMILY_DAILY_LISTINGS:
        infoList.push(
          `This listing will be billed at your daily rate (${formattedPrice}) and included in your monthly invoice`,
        );
        break;
      default:
        break;
    }
  }

  infoList.push('Renters will see the Zillow Rental Manager version of the listing on Zillow, Trulia & HotPads');

  if (contract?.isRequired) {
    infoList.push('You can cancel anytime by deactivating your listing from the listing page');
  }

  return (
    <div data-testid="PreActivationModalWrapper">
      {(activationErrorMessage || (contractErrorMessage && displayPreActivationModal)) && (
        <Modal
          open={true}
          onOpenChange={(open) =>
            !open &&
            (() => {
              setActivationErrorMessage('');
            })()
          }
          header={<Heading level={2}>Something went wrong</Heading>}
          body={
            <div data-testid="PreActivationModalWrapper-error">
              <Alert tone="critical">{activationErrorMessage || contractErrorMessage}</Alert>
            </div>
          }
        />
      )}
      {displayPreActivationModal && !isLoadingContracts && (
        <Modal
          header={<Heading level={2}>Publish this listing?</Heading>}
          body={
            <>
              <Icon size="xl" css={{ color: 'icon.accent.blue.hero' }} render={<IconYardSignFilled />} />
              <Paragraph css={{ textStyle: 'heading-sm', marginBlock: '400' }}>
                Things you need to know before publishing:
              </Paragraph>
              <List.Root data-testid="PreActivationModal-List">
                {infoList.map((item, index) => (
                  <List.Item key={index}>{item}</List.Item>
                ))}
              </List.Root>
              {mustSetActiveContract && (contracts?.length || 0) > 1 && (
                <FormField
                  label={<Label>Active contracts</Label>}
                  control={
                    <Select
                      value={customerId || 'Select'}
                      onChange={(e) => setCustomerId(e.target.value)}
                      css={{ whiteSpace: 'pre' }}
                    >
                      <option disabled={true}>Select</option>
                      {contracts?.map((contract) => (
                        <option key={contract.customerId} value={contract.customerId}>
                          {contract.name}
                        </option>
                      ))}
                    </Select>
                  }
                  css={{ marginBlock: '400' }}
                />
              )}
            </>
          }
          footer={
            <ModalFooter
              submitText="Yes, publish"
              isSubmitDisabled={mustSetActiveContract && effectiveCustomerId === null}
              errorMessage={errorMessage}
              hasError={!!errorMessage}
              onSubmit={handleSubmit}
              onSubmitDataEventId={PayAndPublishCSEvents.ActivateListing.data_event_id}
              onCancel={() => setDisplayPreActivationModal(false)}
              isSubmitting={isSubmitting}
            />
          }
          onOpenChange={(open) => !open && setDisplayPreActivationModal(false)}
          open={true}
          size={{ base: 'fullScreen', md: 'md' }}
        />
      )}
    </div>
  );
};

export default PreActivationModal;
